<script lang="ts">
    import { location, push } from 'svelte-spa-router';
    import { sidebar } from '../stores/sidebar';
    import { auth } from '../stores/auth';
    import { t } from '../stores/locale';
    import Button from './forms/Button.svelte';
    import AboutModal from './AboutModal.svelte';

    export let loginCallback: () => void = () => {};

    let aboutModalOpen = false;

    const LEVEL_COLORS: Record<string, string> = {
        maintenance: '#FFB300',
        technician:  '#2196F3',
        admin:       '#F44336',
    };

    const LEVEL_ICONS: Record<string, string> = {
        maintenance: 'M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z',
        technician:  'M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18',
        admin:       'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
    };

    function navigate(path: string) {
        if ($location !== path) {
            push(path);
        }
        sidebar.close();
    }

    function handleKeydown(e: KeyboardEvent) {
        if (e.key === 'Escape') sidebar.close();
    }

    function handleLogin() {
        loginCallback();
        sidebar.close();
    }
</script>

<svelte:window on:keydown={handleKeydown} />

<!-- Backdrop (mobile only) -->
<!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
<div
    class="sidebar-backdrop"
    class:visible={$sidebar}
    on:click={sidebar.close}
    aria-hidden="true"
></div>

<aside class="sidebar" class:open={$sidebar} aria-label="Main navigation">

    <div class="sidebar-logo">
        <svg width="753" height="183" viewBox="0 0 753 183">
            <polygon class="fil0" points="0,4 18,4 18,117 92,117 92,132 0,132 0,4 "/>
            <polygon class="fil0" points="113,4 131,4 131,132 113,132 113,4 "/>
            <path class="fil0" d="M257 38c-2,-18 -20,-23 -40,-23 -18,0 -34,9 -34,23 0,7 4,14 20,17l0 0 47 8 0 0c21,3 31,15 31,31 0,26 -23,41 -61,41 -36,0 -57,-14 -59,-42l0 0 19 0 0 0c1,17 17,27 41,27 27,0 41,-10 41,-24 0,-35 -97,-3 -97,-57 0,-24 21,-38 55,-38 27,0 55,7 56,37l0 0 -19 0 0 0z"/>
            <polygon class="fil0" points="346,19 292,19 292,4 418,4 418,19 364,19 364,132 346,132 346,19 "/>
            <polygon class="fil0" points="437,4 532,4 532,18 455,18 455,59 527,59 527,73 455,73 455,117 534,117 534,132 437,132 437,4 "/>
            <path class="fil0" d="M681 96c-4,28 -31,39 -62,39 -45,0 -66,-32 -66,-67 0,-31 18,-67 71,-67 30,0 53,15 55,36l0 0 -21 0 0 0c-3,-16 -18,-22 -35,-22 -29,0 -51,17 -51,53 0,36 22,52 51,52 17,0 34,-7 37,-24l0 0 21 0 0 0z"/>
            <path class="fil0" d="M692 26c-7,0 -12,-5 -12,-11 0,-7 5,-12 12,-12 6,0 11,5 11,12 0,6 -5,11 -11,11l0 0 0 0zm0 3c7,0 14,-7 14,-14 0,-8 -7,-14 -14,-14 -8,0 -14,6 -14,14 0,7 6,14 14,14l0 0 0 0zm-1 -14c1,1 2,0 2,1 1,1 1,2 1,4 0,1 1,2 1,3l0 0 3 0 0 0c-1,-2 -1,-4 -1,-5 0,-2 0,-3 -3,-3l0 0 0 -1 0 0c2,0 4,-1 4,-3 0,-4 -4,-5 -7,-5l0 0 -5 0 0 17 3 0 0 -8 2 0 0 0zm-2 -2l0 -4 3 0 0 0c2,0 2,0 2,2 0,1 0,2 -3,2l0 0 -2 0 0 0z"/>
            <polygon class="fil1" points="753,183 753,146 717,146 717,183 753,183 "/>
            <polygon class="fil0" points="0,147 8,147 8,176 29,176 29,182 0,182 0,147 "/>
            <path class="fil0" d="M41 158l0 24 -8 0 0 -24 8 0 0 0zm-8 -11l9 0 0 6 -9 0 0 -6 0 0z"/>
            <path class="fil0" d="M49 163c0,-2 0,-4 -1,-5l0 0 8 0 0 0c0,1 0,2 0,4l0 0 0 0 0 0c3,-4 6,-5 11,-5 5,0 12,2 12,10l0 0 0 15 -8 0 0 -13 0 0c0,-6 -3,-7 -7,-7 -4,0 -8,2 -8,8l0 0 0 12 -7 0 0 -19 0 0z"/>
            <path class="fil0" d="M94 167c0,-4 4,-6 8,-6 4,0 7,2 8,6l0 0 -16 0 0 0zm24 4l0 -1 0 0c0,-10 -8,-13 -16,-13 -13,0 -17,6 -17,13 0,6 4,13 17,13 5,0 14,-2 15,-8l0 0 -8 0 0 0c-1,3 -4,3 -7,3 -4,0 -8,-2 -8,-7l0 0 24 0 0 0z"/>
            <path class="fil0" d="M147 171c-1,6 -6,8 -10,8 -5,0 -6,-3 -6,-4 0,-3 3,-4 9,-4l0 0 7 0 0 0zm7 -4c0,-8 -7,-10 -14,-10 -7,0 -14,1 -15,7l0 0 8 0 0 0c0,-2 3,-3 6,-3 4,0 8,1 8,5l0 0 -7 0 0 0c-10,0 -17,2 -17,9 0,4 3,8 12,8 5,0 9,-2 12,-5 0,2 0,3 0,4l0 0 7 0 0 0c0,-2 0,-3 0,-5l0 0 0 -10 0 0z"/>
            <path class="fil0" d="M163 164c0,-3 0,-5 0,-6l0 0 7 0 0 0c0,1 0,3 0,4l0 0 0 0 0 0c1,-2 3,-5 9,-5 2,0 3,0 5,1l0 0 0 5 0 0c-1,0 -1,0 -2,0 0,0 -1,0 -2,0 -6,0 -9,2 -9,9l0 0 0 10 -8 0 0 -18 0 0z"/>
            <path class="fil0" d="M195 167c1,-4 5,-6 8,-6 5,0 8,2 9,6l0 0 -17 0 0 0zm25 4l0 -1 0 0c0,-10 -9,-13 -16,-13 -13,0 -17,6 -17,13 0,6 4,13 17,13 5,0 14,-2 15,-8l0 0 -8 0 0 0c-1,3 -5,3 -7,3 -4,0 -9,-2 -9,-7l0 0 25 0 0 0z"/>
            <path class="fil0" d="M252 171c1,4 4,7 10,7 7,0 10,-3 10,-6 0,-2 -2,-3 -6,-4l0 0 -8 -1 0 0c-10,-1 -13,-5 -13,-10 0,-8 8,-11 17,-11 11,0 16,4 17,11l0 0 -9 0 0 0c0,-4 -3,-6 -9,-6 -3,0 -7,2 -7,5 0,3 2,4 5,4l0 0 11 2 0 0c9,1 10,6 10,9 0,7 -6,12 -19,12 -10,0 -17,-5 -17,-12l0 0 8 0 0 0z"/>
            <path class="fil0" d="M294 167c0,-4 4,-6 8,-6 4,0 8,2 8,6l0 0 -16 0 0 0zm24 4l0 -1 0 0c0,-10 -8,-13 -16,-13 -13,0 -16,6 -16,13 0,6 3,13 16,13 6,0 15,-2 15,-8l0 0 -8 0 0 0c-1,3 -4,3 -6,3 -5,0 -9,-2 -9,-7l0 0 24 0 0 0z"/>
            <path class="fil0" d="M325 163c0,-2 0,-4 0,-5l0 0 7 0 0 0c0,1 0,2 0,4l0 0 0 0 0 0c3,-4 6,-5 11,-5 5,0 12,2 12,10l0 0 0 15 -8 0 0 -13 0 0c0,-6 -3,-7 -7,-7 -4,0 -7,2 -7,8l0 0 0 12 -8 0 0 -19 0 0z"/>
            <path class="fil0" d="M370 174c0,3 2,4 6,4 4,0 6,-1 6,-3 0,-1 -1,-2 -3,-2l0 0 -7 -1 0 0c-8,-1 -10,-5 -10,-7 0,-4 3,-8 14,-8 6,0 14,1 14,8l0 0 -9 0 0 0c0,-3 -2,-4 -6,-4 -3,0 -5,1 -5,3 0,1 1,2 5,2l0 0 7 1 0 0c6,1 8,4 8,8 0,4 -3,8 -13,8 -11,0 -15,-2 -16,-9l0 0 9 0 0 0z"/>
            <path class="fil0" d="M412 161c6,0 8,4 8,9 0,4 -2,8 -8,8 -6,0 -9,-4 -9,-8 0,-5 3,-9 9,-9l0 0 0 0zm0 -4c-13,0 -17,6 -17,13 0,6 4,13 17,13 13,0 17,-7 17,-13 0,-7 -4,-13 -17,-13l0 0 0 0z"/>
            <path class="fil0" d="M435 164c0,-3 0,-5 0,-6l0 0 7 0 0 0c0,1 1,3 1,4l0 0 0 0 0 0c0,-2 2,-5 8,-5 2,0 3,0 5,1l0 0 0 5 0 0c0,0 -1,0 -1,0 -1,0 -2,0 -2,0 -7,0 -10,2 -10,9l0 0 0 10 -8 0 0 -18 0 0z"/>
            <polygon class="fil0" points="511,147 511,153 497,153 497,182 489,182 489,153 475,153 475,147 511,147 "/>
            <path class="fil0" d="M516 167c1,-4 5,-6 9,-6 4,0 7,2 8,6l0 0 -17 0 0 0zm25 4l0 -1 0 0c0,-10 -9,-13 -16,-13 -13,0 -17,6 -17,13 0,6 4,13 17,13 5,0 14,-2 15,-8l0 0 -8 0 0 0c-1,3 -4,3 -7,3 -4,0 -9,-2 -9,-7l0 0 25 0 0 0z"/>
            <path class="fil0" d="M567 166c0,-3 -2,-5 -6,-5 -4,0 -7,3 -7,9 0,4 2,8 7,8 4,0 6,-1 7,-5l0 0 8 0 0 0c0,7 -7,10 -15,10 -10,0 -15,-5 -15,-13 0,-9 6,-13 15,-13 10,0 14,4 15,9l0 0 -9 0 0 0z"/>
            <path class="fil0" d="M581 147l8 0 0 15 0 0 0 0c3,-4 6,-5 11,-5 4,0 12,2 12,10l0 0 0 15 -8 0 0 -13 0 0c0,-6 -3,-7 -7,-7 -4,0 -8,2 -8,8l0 0 0 12 -8 0 0 -35 0 0z"/>
            <path class="fil0" d="M619 163c0,-2 0,-4 0,-5l0 0 8 0 0 0c0,1 0,2 0,4l0 0 0 0 0 0c2,-4 6,-5 11,-5 4,0 11,2 11,10l0 0 0 15 -7 0 0 -13 0 0c0,-6 -3,-7 -7,-7 -4,0 -8,2 -8,8l0 0 0 12 -8 0 0 -19 0 0z"/>
            <path class="fil0" d="M665 158l0 24 -8 0 0 -24 8 0 0 0zm-8 -11l8 0 0 6 -8 0 0 -6 0 0z"/>
            <polygon class="fil0" points="681,147 681,167 693,158 703,158 690,168 706,182 695,182 681,168 681,182 673,182 673,147 681,147 "/>
            <path class="fil0" d="M729 111c-5,0 -7,4 -7,7 0,5 4,11 13,11 6,0 13,-3 13,-11 0,-3 -1,-5 -2,-8l0 0 -7 0 0 9 -5 0 0 -14 16 -1 0 0c2,4 3,10 3,14 0,10 -5,17 -18,17 -11,0 -18,-6 -18,-16 0,-8 4,-14 12,-14l0 0 0 6 0 0z"/>
            <path class="fil0" d="M734 98c-2,0 -4,1 -6,1l0 0 0 -6 0 0c1,0 3,0 4,0 -3,-2 -5,-4 -5,-8 0,-3 2,-6 6,-8 -4,-2 -6,-5 -6,-8 0,-3 2,-9 11,-9l0 0 14 0 0 6 -12 0 0 0c-6,0 -8,2 -8,5 0,3 3,6 9,6l0 0 11 0 0 5 -12 0 0 0c-6,0 -8,2 -8,5 0,3 3,6 9,6l0 0 11 0 0 5 -18 0 0 0z"/>
            <path class="fil0" d="M740 49c-4,0 -8,-2 -8,-6 0,-3 4,-6 8,-6 5,0 9,3 9,6 0,4 -4,6 -9,6l0 0 0 0zm7 6c2,0 4,0 5,0l0 0 0 -5 0 0c-1,0 -2,-1 -3,-1l0 0 0 0 0 0c3,-1 4,-4 4,-7 0,-7 -6,-11 -13,-11 -10,0 -13,7 -13,11 0,2 1,5 4,7l0 0 0 0 -13 0 0 6 29 0 0 0z"/>
            <polygon class="fil0" points="718,27 718,21 732,21 732,6 718,6 718,0 752,0 752,6 737,6 737,21 752,21 752,27 718,27 "/>
        </svg>
    </div>

    <!-- Auth widget -->
    <div class="sidebar-auth">
        {#if $auth.level}
            <div class="user-card-wrapper">
                <div class="user-card">
                    <div class="user-avatar" style="background-color: {LEVEL_COLORS[$auth.level]}20; color: {LEVEL_COLORS[$auth.level]}">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                            width="20" height="20" aria-hidden="true">
                            <path d={LEVEL_ICONS[$auth.level]} />
                        </svg>
                    </div>
                    <div class="user-info">
                        <span class="user-role" style="color: {LEVEL_COLORS[$auth.level]}">{$auth.level}</span>
                    </div>
                </div>
                <div class="auth-action-row">
                    <Button variant="default" fullWidth on:click={auth.logout} aria-label="Log out">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                            <polyline points="16 17 21 12 16 7"/>
                            <line x1="21" y1="12" x2="9" y2="12"/>
                        </svg>
                        Log out
                    </Button>
                </div>
            </div>
        {:else}
            <div class="user-card logged-out">
                <div class="auth-login-row">
                    <Button variant="default" fullWidth on:click={handleLogin}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                            <polyline points="10 17 15 12 10 7"/>
                            <line x1="15" y1="12" x2="3" y2="12"/>
                        </svg>
                        Log in
                    </Button>
                </div>
            </div>
        {/if}
    </div>

    <nav class="sidebar-nav">
        <ul>
            <li>
                <Button
                    variant="nav"
                    class="{$location === '/' ? 'active' : ''}"
                    on:click={() => navigate('/')}
                >
                    {$t('nav.dashboard')}
                </Button>
            </li>
            <li>
                <Button
                    variant="nav"
                    class="{$location === '/system' ? 'active' : ''}"
                    on:click={() => navigate('/system')}
                >
                    {$t('nav.system')}
                </Button>
            </li>
            <li>
                <Button
                    variant="nav"
                    class="{$location === '/modules' ? 'active' : ''}"
                    on:click={() => navigate('/modules')}
                >
                    {$t('nav.modules')}
                </Button>
            </li>
            <li>
                <Button
                    variant="nav"
                    class="{$location === '/logs' ? 'active' : ''}"
                    on:click={() => navigate('/logs')}
                >
                    {$t('nav.logs')}
                </Button>
            </li>
            <li>
                <Button
                    variant="nav"
                    class="{$location === '/status' ? 'active' : ''}"
                    on:click={() => navigate('/status')}
                >
                    {$t('nav.status')}
                </Button>
            </li>
            <li>
                <Button
                    variant="nav"
                    class="{$location === '/forms' ? 'active' : ''}"
                    on:click={() => navigate('/forms')}
                >
                    {$t('nav.forms')}
                </Button>
            </li>
            <li>
                <Button
                    variant="nav"
                    class="{$location === '/modals' ? 'active' : ''}"
                    on:click={() => navigate('/modals')}
                >
                    {$t('nav.modals')}
                </Button>
            </li>
            <li>
                <Button
                    variant="nav"
                    class="{$location === '/settings' ? 'active' : ''}"
                    on:click={() => navigate('/settings')}
                >
                    {$t('nav.settings')}
                </Button>
            </li>
            {#if $auth.level === 'technician' || $auth.level === 'admin'}
                <li>
                    <Button
                        variant="nav"
                        class="{$location === '/terminal' ? 'active' : ''}"
                        on:click={() => navigate('/terminal')}
                    >
                        {$t('nav.terminal')}
                    </Button>
                </li>
            {/if}
        </ul>
    </nav>

    <footer class="sidebar-footer-wrapper">
        <div class="sidebar-footer">
            <div class="footer-info">
                <span class="version-label">System</span>
                <span class="version-value">0.0.1-DEV</span>
            </div>
             <Button variant="icon" size="sm" on:click={() => aboutModalOpen = true} title="About" aria-label="About">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
            </Button>
        </div>
    </footer>
</aside>

<AboutModal bind:open={aboutModalOpen} />

<style lang="scss">
    .sidebar-backdrop {
        display: none;
        position: fixed;
        inset: 0;
        background-color: rgba(0, 0, 0, 0.45);
        z-index: 199;
        transition: opacity 0.25s ease;

        &.visible {
            display: block;
        }

        @media (min-width: $breakpoint-md) {
            display: none !important;
        }
    }

    .sidebar {
        width: $sidebar-width;
        background-color: $color-sidebar-bg;
        /* border-right: 1px solid $color-header-border; */
        overflow-y: auto;
        flex-shrink: 0;
        display: flex;
        flex-direction: column;
        transition: background-color 0.2s ease, border-color 0.2s ease;

        // Mobile: fixed overlay sliding in from the left
        @media (max-width: #{$breakpoint-md - 1px}) {
            position: fixed;
            top: 0;
            left: 0;
            height: 100%;
            z-index: 200;
            transform: translateX(-100%);
            transition:
                transform 0.28s cubic-bezier(0.4, 0, 0.2, 1),
                background-color 0.2s ease,
                border-color 0.2s ease;

            &.open {
                transform: translateX(0);
            }
        }

        // Desktop: always visible, sits in the flex layout
        @media (min-width: $breakpoint-md) {
            position: static;
            transform: none;
        }
    }

    // ── Auth widget ───────────────────────────────────────────────────────────

    .sidebar-auth {
        padding: 1em 1em 0.5em;
    }

    .sidebar-logo {
        margin: 1rem 1rem 0.5rem;
        padding: 1.5rem 1.25rem;
        background-color: #1a1a1a; // Dark background to ensure white logo is visible
        border-radius: $border-radius-large;
        display: flex;
        align-items: center;
        justify-content: center;

        svg {
            width: 100%;
            height: auto;
            display: block;
        }

        .fil1 { fill: #D20000; }
        .fil0 { fill: white; }
    }


    .user-card-wrapper {
        background-color: $color-bg-offset;
        padding: 1rem 0.75rem;
        border-radius: $border-radius-large;
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
    }

    .user-card {
        display: flex;
        align-items: center;
        gap: 0.75rem;
    }

    .auth-action-row, .auth-login-row {
        width: 100%;
    }
    
    // Login button container (when logged out)
    .user-card.logged-out {
        background-color: $color-bg-offset;
        padding: 1rem 0.75rem;
        border-radius: $border-radius-large;
    }

    .user-avatar {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .user-info {
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: center;
        min-width: 0;
    }

    .user-role {
        font-size: 0.9rem;
        font-weight: 600;
        text-transform: capitalize;
        line-height: 1.2;
        text-align: center;
    }

    :global(.sidebar-auth .auth-action-row .btn),
    :global(.sidebar-auth .auth-login-row .btn) {
        width: 100%;
        justify-content: center;
        gap: 0.5rem;
    }

    // ── Nav ───────────────────────────────────────────────────────────────────

    .sidebar-nav {
        flex: 1;
        padding: 0.5em 1em; // More horizontal padding

        ul {
            list-style: none;
            margin: 0;
            padding: 0;
            display: flex;
            flex-direction: column;
            gap: 0.25em; // Gap between nav items
        }
    }

    .sidebar-footer-wrapper {
        padding: 1em;
    }

    .sidebar-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.75rem 1rem;
        background-color: $color-bg-offset; 
        border-radius: $border-radius-large;
        transition: background-color 0.2s ease;
    }

    .footer-info {
        display: flex;
        flex-direction: column;
        line-height: 1.2;
    }

    .version-label {
        font-size: 0.7rem;
        color: $color-text-muted;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        font-weight: 600;
    }

    .version-value {
        font-size: 0.8rem;
        color: $color-text;
        font-family: $font-family-mono;
    }
</style>
